import { useEffect, useMemo, useRef } from 'react'
import useSearchAnalytics from 'hooks/Analytics/useSearchAnalytics'
import useMerchandiseFilterWasApplied from './useMerchandiseFilterWasApplied'
import useMerchandiseSortWasApplied from './useMerchandiseSortWasApplied'
import useMerchandiseSearchEventType from './useMerchandiseSearchEventType'
import {
  mapMerchandiseFiltersToSearchContext,
  mapMerchandiseFiltersToFilterContexts,
  mapMerchandiseFiltersToSortContext,
} from 'analytics/mapMerchandiseSearchTracking'
import uuidV4 from 'lib/string/uuidV4Utils'

interface Props {
  filters: App.MerchandiseFilters
  fetching: boolean
  urlPathSlug?: string
}

function useMerchandiseSearchTracking({ filters, fetching, urlPathSlug }: Props) {
  const searchIdRef = useRef<string>(uuidV4())
  const previousQueryRef = useRef<string | undefined>(filters.name)

  if (filters.name !== previousQueryRef.current) {
    searchIdRef.current = uuidV4()
    previousQueryRef.current = filters.name
  }

  const searchContext = useMemo(
    () => mapMerchandiseFiltersToSearchContext({
      filters,
      searchId: searchIdRef.current,
      urlPathSlug,
    }),
    [filters, urlPathSlug],
  )

  const filterContexts = useMemo(
    () => mapMerchandiseFiltersToFilterContexts(filters),
    [filters],
  )

  const sortContext = useMemo(
    () => mapMerchandiseFiltersToSortContext(filters),
    [filters],
  )

  const sortWasApplied = useMerchandiseSortWasApplied(filters)
  const filterWasApplied = useMerchandiseFilterWasApplied(filters)

  const searchEvent = useMerchandiseSearchEventType({
    sortWasApplied,
    filterWasApplied,
  })

  const { trackSearchEvent } = useSearchAnalytics({
    searchEvent,
    searchContext,
    filterContexts,
    sortContext,
  })

  const searchKey = useMemo(() => JSON.stringify({
    name: filters.name,
    categories: filters.categories,
    brands: filters.brands,
    campaigns: filters.campaigns,
    tags: filters.tags,
    price: filters.price,
    options: filters.options,
    sortBy: filters.sortBy,
  }), [filters])

  const trackedKey = useRef<string | null>(null)

  useEffect(() => {
    if (fetching) return

    const isNewSearch = searchKey !== trackedKey.current
    if (isNewSearch) {
      trackSearchEvent()
      trackedKey.current = searchKey
    }
  }, [fetching, searchKey, trackSearchEvent])

  return {
    searchContext,
    filterContexts,
    sortContext,
  }
}

export default useMerchandiseSearchTracking
